import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { Image3D } from '~/domain/patient/image3d';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { Environment } from '~/engine/data/Environment';
import {
    bodyContourTextureFromMask,
    ThresholdSegmentationStats,
    thresholdSegmentationTextureStats,
} from '~/engine/util/algorithms/sdf/BodyContourExtraction';
import { dicomColorToVec3 } from '~/utils/color';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { usePreviewBase } from '~/hooks/structure-set/usePreviewBase';
import {
    BodyContourMaskCache,
    BodyContourParams,
    THRESHOLD_TOOL_HU_ELIGIBILITY,
    ThresholdToolEligibility,
    bodyContourPreviewKey,
    resolveKeepInsideTexture,
    toBodyContourTextureStages,
} from './bodyContourParams';

interface UseBodyContourPreviewOptions {
    environment: Environment | null;
    /** Domain image the extraction reads raw values from (must satisfy
     *  the tool's eligibility gate below). */
    image: Image3D | undefined;
    /** Shared with the apply hook so both reuse the same CPU mask. */
    maskCache: BodyContourMaskCache;
    /** Voxel-unit gate + refusal message. Defaults to the CT tool's HU
     *  gate; the SUV tool passes its SUVbw gate. Must match the
     *  maskCache's quantification — the cache is the typed backstop. */
    eligibility?: ThresholdToolEligibility;
}

/** Per-preset options of a single preview run. Not part of the preview
 *  KEY: neither changes what is computed, only how it is presented and
 *  what is reported about it. */
export interface BodyContourPreviewOptions {
    /**
     * Whether publishing the preview recedes the regular structures
     * (default true, the historical behavior) — see the note on
     * {@link CustomTargetObject.dimsOtherStructures}.
     */
    dimsOtherStructures?: boolean;
    /**
     * Whether the reported statistics include the connected-component
     * count (default false). Preset-driven — see
     * {@link ThresholdSegmentationPreset.reportComponents} — so the
     * presets that do not display the count never pay for the labeling
     * pass that produces it.
     */
    reportComponents?: boolean;
}

/** The stats of the preview CURRENTLY held, tagged with the key of the
 *  preview they describe. The tag is what keeps them in lockstep with
 *  the held result: see `previewStats` below. */
interface HeldStats {
    key: string;
    stats: ThresholdSegmentationStats;
}

/**
 * Preview lifecycle for the body-contour operation. Unlike margin/ring the
 * input is the image, not a source ROI — the abort/held-result mechanics are
 * the shared {@link usePreviewBase} ones. An extraction that returns
 * undefined (GL contention / failed allocation) clears the preview rather
 * than leaving a stale outline that no longer matches the parameters, and
 * an EMPTY mask (box off the volume / in air, extreme threshold) is never
 * published at all — a blank overlay would be indistinguishable from
 * "preview did nothing", and refusing to hold it also guarantees
 * {@link consumePreview} can never hand an empty texture to apply.
 *
 * A run starts only when a caller requests one: when it runs is the
 * caller's policy, not this hook's. The caller derives which parameter
 * set the on-screen preview belongs to from `heldKey`, which tracks the
 * ACTUALLY held preview through every outcome — published, cleared on
 * failure, kept on abort — so no caller can attribute a preview to
 * parameters a failure path has already destroyed it for. The preview COLOR is deliberately
 * outside the key: recoloring is a render-only concern
 * ({@link recolorPreview}), so a color change never marks the preview
 * stale. Recoloring repaints THIS preview's wrapper only — no ROI's
 * color is ever written by the preview path, on any preset.
 *
 * The per-run {@link BodyContourPreviewOptions} carry what the PRESET
 * decides about a preview rather than what it computes: whether
 * publishing it recedes the regular structures (the recede re-tints them
 * rather than fading them — see
 * {@link CustomTargetObject.dimsOtherStructures} — so a preset whose
 * result is a different structure passes false), and whether the
 * reported statistics carry a connected-component count. Neither is part
 * of the preview key: neither changes the result.
 *
 * A published preview also carries `previewStats` — what the field
 * behind the outline contains, in domain units — so the panel can state
 * a volume the user can check a visually sparse result against.
 */
export function useBodyContourPreview({
    environment,
    image,
    maskCache,
    eligibility = THRESHOLD_TOOL_HU_ELIGIBILITY,
}: UseBodyContourPreviewOptions) {
    const intl = useIntl();
    const {
        isPreviewing,
        beginPreviewRun,
        endPreviewRun,
        updateView,
        beginOperation,
        setResult,
        consumeResult,
        recolorResult,
        abortInFlight,
        cleanup,
        heldKey,
    } = usePreviewBase({ environment });

    /**
     * Statistics of the preview last PUBLISHED, tagged with its key.
     * Written only where a preview is published — never on the failure,
     * abort or refusal paths — and read back through the key comparison
     * in `previewStats`, so the numbers the panel can show are exactly
     * those of the preview {@link usePreviewBase} is holding: cleared
     * with it (failure, tab switch, consume by apply, unmount), replaced
     * with it, and kept while it is merely OUTDATED — a stale preview is
     * still the outline on screen, and its own numbers are what describe
     * it.
     */
    const [heldStats, setHeldStats] = useState<HeldStats | null>(null);

    const handlePreview = useCallback(
        async (
            params: BodyContourParams,
            color: DicomColor,
            options: BodyContourPreviewOptions = {}
        ): Promise<boolean> => {
            if (!environment?.image || !image) {
                return true;
            }
            if (!eligibility.isQuantified(image)) {
                // Defense in depth behind the toolbar/panel gating —
                // reachable only through stale state (the image switched
                // to an ineligible one under an in-flight interaction).
                // SURFACED, not a silent return: a click that does
                // nothing hides that the tool refused, and the mask cache
                // backs this up with the tool's typed domain error.
                enqueueSnackbar(
                    intl.formatMessage({ id: eligibility.refusalMessageId }),
                    { variant: 'error' }
                );
                return true;
            }

            const signal = beginOperation();
            const runId = beginPreviewRun();

            // Same main-thread yield the other SDF previews use so the busy
            // state paints before the compute starts.
            await waitForUI();

            try {
                const mask = await maskCache.getOrExtract(
                    image,
                    params,
                    signal
                );
                if (mask.isEmpty) {
                    // Nothing was classified body: say why the viewport
                    // shows no outline instead of publishing a blank
                    // preview, and drop any held preview so a stale
                    // outline cannot masquerade as this result.
                    cleanup();
                    enqueueSnackbar(
                        intl.formatMessage({ id: 'thresholdSeg.previewEmpty' }),
                        { variant: 'warning' }
                    );
                    return true;
                }
                const texture = await bodyContourTextureFromMask(
                    image.grid,
                    mask,
                    {
                        ...toBodyContourTextureStages(params),
                        keepInsideTexture: resolveKeepInsideTexture(
                            environment,
                            params
                        ),
                    },
                    signal
                );

                if (!texture) {
                    // GL contention or a failed allocation: report the
                    // deferral to the caller, which re-offers the run.
                    // The displayed outline stays up and nothing is said
                    // — clearing would blank the viewports for one retry
                    // cycle over a condition that heals itself, and a run
                    // nobody asked for has no request to answer.
                    return false;
                }
                if (signal.aborted) {
                    texture.delete();
                    // Superseded, not deferred: the run that replaced this
                    // one owns the outcome, so this must not ask to be
                    // re-offered.
                    return true;
                }
                if (texture.isEmpty) {
                    // The pipeline can empty a non-empty mask (the
                    // keep-inside clip with a disjoint EXTERNAL): same
                    // refusal as an empty mask — a blank overlay would
                    // masquerade as "preview did nothing".
                    texture.delete();
                    cleanup();
                    enqueueSnackbar(
                        intl.formatMessage({ id: 'thresholdSeg.previewEmpty' }),
                        { variant: 'warning' }
                    );
                    return true;
                }

                // Statistics of the field THIS texture holds, read before
                // it is handed to the wrapper and never recomputed after
                // — so the numbers the panel shows and the outline the
                // viewport draws are the same field, past every stage.
                // Release-with-acquisition: the texture is ours until
                // setResult takes it, so an abort thrown mid-count must
                // not strand it.
                let stats: ThresholdSegmentationStats;
                try {
                    stats = await thresholdSegmentationTextureStats(
                        texture,
                        { countComponents: options.reportComponents === true },
                        signal
                    );
                } catch (e) {
                    texture.delete();
                    throw e;
                }
                if (signal.aborted) {
                    texture.delete();
                    // Superseded, not deferred: the run that replaced this
                    // one owns the outcome, so this must not ask to be
                    // re-offered.
                    return true;
                }

                // The wrapper owns a FRESHLY generated texture — never the
                // keep-inside EXTERNAL's own ContourTexture, which the
                // pipeline reads but never returns (its lifetime belongs
                // to the ROI, and the preview slot deletes what it holds).
                const previewObj = new CustomTargetObject(
                    environment.image,
                    texture,
                    dicomColorToVec3(color)
                );
                previewObj.renderOptions = { fillAlpha: 0.18 };
                previewObj.dimsOtherStructures =
                    options.dimsOtherStructures ?? true;
                const key = bodyContourPreviewKey(image, params);
                setResult(previewObj, key);
                setHeldStats({ key, stats });
                return true;
            } catch (e) {
                if ((e as DOMException)?.name === 'AbortError') {
                    // Superseded by a newer preview — not an error, and
                    // not a deferral: the newer run owns the outcome.
                    return true;
                }
                console.error('Threshold segmentation preview error:', e);
                cleanup();
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'thresholdSeg.previewFailed' },
                        { error: getErrorMessage(e) }
                    ),
                    { variant: 'error' }
                );
                // A genuine failure is settled, not deferred: re-offering
                // it would restart the same failing run forever.
                return true;
            } finally {
                // Superseded runs never clear the flag: a later run is
                // still computing, and dropping "busy" would re-enable
                // Apply and lift the panel's close prevention mid-compute.
                endPreviewRun(runId);
            }
        },
        [
            environment,
            image,
            maskCache,
            eligibility,
            beginOperation,
            setResult,
            cleanup,
            beginPreviewRun,
            endPreviewRun,
            intl,
        ]
    );

    const consumePreview = useCallback(
        (params: BodyContourParams): ContourTexture | undefined =>
            image
                ? consumeResult(bodyContourPreviewKey(image, params))
                : undefined,
        [consumeResult, image]
    );

    /** Repaint the held preview in a new color — no recompute, no
     *  staleness: the underlying texture is untouched. No-op while no
     *  preview is held. */
    const recolorPreview = useCallback(
        (color: DicomColor) => {
            recolorResult(dicomColorToVec3(color));
        },
        [recolorResult]
    );

    /**
     * Statistics of the preview ACTUALLY held right now, or null when
     * none is. DERIVED from `heldKey` rather than cleared by hand at
     * every exit: `heldKey` is what {@link usePreviewBase} keeps in
     * lockstep with the held result itself, so a preview the panel
     * cleared, apply consumed or a failure path destroyed takes its
     * numbers with it by construction — there is no path that can drop
     * the outline and leave a volume behind claiming to describe it.
     */
    const previewStats: ThresholdSegmentationStats | null =
        heldStats !== null && heldStats.key === heldKey
            ? heldStats.stats
            : null;

    return useMemo(
        () => ({
            isPreviewing,
            handlePreview,
            consumePreview,
            recolorPreview,
            abortInFlight,
            cleanup,
            updateView,
            heldKey,
            previewStats,
        }),
        [
            isPreviewing,
            handlePreview,
            consumePreview,
            recolorPreview,
            abortInFlight,
            cleanup,
            updateView,
            heldKey,
            previewStats,
        ]
    );
}
